import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useIsAdmin } from './useAdmin';
import { applyBrandTheme, clearBrandTheme } from '@/lib/brand-theme';

export interface Brand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  email_domain: string | null;
  custom_domain: string | null;
  domain_status: string | null;
  settings: Record<string, unknown> | null;
  features: Record<string, boolean> | null;
}

export type BrandRole = 'member' | 'brand_admin';

export interface UserBrandWithRole {
  brand: Brand;
  role: BrandRole;
}

interface BrandContextType {
  /** The currently active brand (null = "All Brands" for admins) */
  activeBrandId: string | null;
  /** All brands the current user has access to */
  brands: Brand[];
  /** User's brands with their role info */
  userBrandsWithRoles: UserBrandWithRole[];
  /** Whether brands are still loading */
  isLoading: boolean;
  /** Switch the active brand */
  setActiveBrandId: (brandId: string | null) => void;
  /** Whether the user can see the "All Brands" option (admin only) */
  canViewAll: boolean;
  /** Helper: current brand object */
  activeBrand: Brand | null;
  /** True when brand is locked by custom domain (no switching allowed) */
  isDomainLocked: boolean;
  /** True when user is on a custom domain but doesn't have access to that brand */
  isDomainMismatch: boolean;
  /** Whether current user is a brand admin for the active brand */
  isBrandAdmin: boolean;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

const STORAGE_KEY = 'active-brand-id';

function isPlatformDomain(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.vercel.app') ||
    hostname === 'franklyadvisors.com' ||
    hostname === 'www.franklyadvisors.com' ||
    hostname === 'app.franklyadvisors.com' ||
    hostname === 'dev.franklyadvisors.com' ||
    hostname === 'dev-app.franklyadvisors.com' ||
    hostname === 'prototype.franklyadvisors.com'
  );
}

/** Check if the current hostname is a known brand custom domain */
function detectDomainBrand(brands: Brand[]): Brand | null {
  const hostname = window.location.hostname;
  if (isPlatformDomain(hostname)) return null;
  return brands.find(b => b.custom_domain === hostname && b.domain_status === 'active') ?? null;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: isAdmin } = useIsAdmin();

  // Fetch user's brands via user_brands join (including role)
  // NOTE: Only select columns that exist on the production brands table.
  // New columns (resend_domain_id, sending_domain_status, etc.) are fetched
  // separately by the components that need them.
  const { data: userBrandsRaw = [], isLoading } = useQuery({
    queryKey: ['user-brands', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_brands')
        .select('brand_id, is_default, role, brands(id, name, slug, logo_url, email_domain, custom_domain, domain_status, settings, features)')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching user brands:', error);
        return [];
      }

      return (data ?? [])
        .filter((ub: unknown) => (ub as { brands: Brand | null }).brands)
        .map((ub: unknown) => ({
          brand: (ub as { brands: Brand }).brands,
          role: ((ub as { role?: string }).role || 'member') as BrandRole,
        }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
  });

  // Agency admins: fetch ALL brands so they can switch to any brand
  const { data: allBrandsRaw = [], isLoading: isLoadingAllBrands } = useQuery({
    queryKey: ['all-brands-for-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name, slug, logo_url, email_domain, custom_domain, domain_status, settings, features')
        .order('name');
      if (error) {
        console.error('Error fetching all brands:', error);
        return [];
      }
      return data as Brand[];
    },
    enabled: isAdmin === true,
    staleTime: 5 * 60_000,
  });

  // For admins, merge all brands with any explicit role assignments
  const userBrandsWithRoles: UserBrandWithRole[] = isAdmin === true
    ? allBrandsRaw.map((brand) => {
        const explicit = userBrandsRaw.find((ubr: UserBrandWithRole) => ubr.brand.id === brand.id);
        return { brand, role: explicit?.role ?? ('member' as BrandRole) };
      })
    : userBrandsRaw;
  const brands: Brand[] = userBrandsWithRoles.map((ubr) => ubr.brand);

  // Restore persisted brand selection
  const [activeBrandId, setActiveBrandIdRaw] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Persist selection
  const setActiveBrandId = (brandId: string | null) => {
    setActiveBrandIdRaw(brandId);
    try {
      if (brandId) {
        localStorage.setItem(STORAGE_KEY, brandId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* noop */ }
  };

  // Detect if we're on a brand's custom domain
  const domainBrand = brands.length > 0 ? detectDomainBrand(brands) : null;
  // Only lock to domain brand if user has no other brands to switch to
  const isDomainLocked = domainBrand !== null && brands.length <= 1;

  // Detect domain mismatch: user is on a custom domain but doesn't have access to that brand
  const isCustomDomain = !isPlatformDomain(window.location.hostname);
  const isDomainMismatch = isCustomDomain && brands.length > 0 && !domainBrand;

  // Auto-select brand: domain brand as default, but allow switching if user has multiple
  useEffect(() => {
    if (domainBrand && isDomainLocked) {
      if (activeBrandId !== domainBrand.id) {
        setActiveBrandId(domainBrand.id);
      }
      return;
    }
    // On custom domain with multiple brands: auto-select domain brand only if none selected
    if (domainBrand && !activeBrandId) {
      setActiveBrandId(domainBrand.id);
      return;
    }
    if (brands.length > 0 && activeBrandId === null && !isAdmin) {
      setActiveBrandId(brands[0].id);
    }
    // If persisted brand isn't in user's list, reset
    if (brands.length > 0 && activeBrandId && !brands.find(b => b.id === activeBrandId) && !isAdmin) {
      setActiveBrandId(brands[0].id);
    }
  }, [brands, activeBrandId, isAdmin, domainBrand, isDomainLocked]);

  const activeBrand = brands.find(b => b.id === activeBrandId) ?? null;
  const isBrandAdmin = activeBrandId
    ? userBrandsWithRoles.some(ubr => ubr.brand.id === activeBrandId && ubr.role === 'brand_admin')
    : false;

  // Apply brand accent color to CSS custom properties
  const accentColor = (activeBrand?.settings as Record<string, unknown> | null)?.accent_color as string | undefined;
  useEffect(() => {
    if (accentColor) {
      applyBrandTheme(accentColor);
    } else {
      clearBrandTheme();
    }
    return () => clearBrandTheme();
  }, [accentColor]);

  return (
    <BrandContext.Provider
      value={{
        activeBrandId,
        brands,
        userBrandsWithRoles,
        isLoading: isAdmin === true ? (isLoading || isLoadingAllBrands) : isLoading,
        setActiveBrandId: isDomainLocked ? () => {} : setActiveBrandId,
        canViewAll: isDomainLocked ? false : isAdmin === true,
        activeBrand,
        isDomainLocked,
        isDomainMismatch,
        isBrandAdmin,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within a BrandProvider');
  return ctx;
}

/**
 * Helper to add brand_id filter to a Supabase query builder.
 * If brandId is null (admin viewing all), no filter is applied.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function scopeByBrand(
  query: unknown,
  brandId: string | null,
  column = 'brand_id',
): unknown {
  if (!brandId) return query;
  return query.eq(column, brandId);
}
